import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const PRESET_AMOUNTS = [5, 10, 25, 50, 100];
const MIN_RECHARGE_DOLLARS = 5;

export function SettingsBillingRecharge() {
  const [amount, setAmount] = useState<number | undefined>(MIN_RECHARGE_DOLLARS);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (amount === undefined || amount < MIN_RECHARGE_DOLLARS) {
      toast.error("Amount must be at least $5");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch("/api/billing/recharge", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: Math.round(amount * 100) / 100 }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Failed to start recharge");
      }

      const data = await response.json();
      if (!data.url) {
        throw new Error("Failed to start recharge");
      }

      window.location.href = data.url;
    } catch (error) {
      console.error("Error starting recharge:", error);
      toast.error(error instanceof Error ? error.message : "Failed to start recharge");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Recharge</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Amount</Label>
            <div className="flex flex-wrap gap-2">
              {PRESET_AMOUNTS.map((preset) => (
                <Button
                  key={preset}
                  type="button"
                  variant={amount === preset ? "default" : "outline"}
                  onClick={() => setAmount(preset)}
                  disabled={isSubmitting}
                >
                  ${preset}
                </Button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="recharge-amount">Custom amount ($)</Label>
            <Input
              id="recharge-amount"
              type="number"
              step="0.01"
              min={MIN_RECHARGE_DOLLARS}
              value={amount !== undefined ? amount : ""}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "" || value === ".") {
                  setAmount(undefined);
                  return;
                }
                const numValue = parseFloat(value);
                if (!isNaN(numValue)) {
                  setAmount(Math.round(numValue * 100) / 100);
                }
              }}
              placeholder="Enter amount"
              disabled={isSubmitting}
            />
            <p className="text-sm text-muted-foreground">Minimum $5</p>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={handleSubmit}
              disabled={
                isSubmitting || amount === undefined || amount < MIN_RECHARGE_DOLLARS
              }
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Redirecting...
                </>
              ) : (
                `Pay ${amount !== undefined ? `$${amount.toFixed(2)}` : ""}`
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
